import * as MaplibreGL from 'maplibre-gl'
import type { StyleSpecification } from '@maplibre/maplibre-gl-style-spec'
import GA from 'react-ga4'
import config from './../config'

export interface NBMapOptions extends Omit<maplibregl.MapOptions, 'style'> {
  style: StyleSpecification | string
  // for customers who are, by contract, allowed to remove NextBillion.ai from the attribution control
  hideAttribution?: boolean
}

/**
 * @example
 * nextbillion.setApiKey("your_api_key")
 * var map = new nextbillion.maps.Map({
 *   container: 'map',
 *   center: [-122.420679, 37.772537],
 *   zoom: 13,
 *   style: 'https://api.nextbillion.io/maps/streets/style.json',
 *   hash: true,
 * });
 *
 */
export class NBMap {
  private readonly _map: MaplibreGL.Map
  private readonly _options: NBMapOptions

  constructor(options: NBMapOptions) {
    if (!config.API_KEY) {
      const msg = 'An API Key is required to create a map!'
      alert(msg)
      throw new Error(
        msg +
          ' For example, add this line of code before creating a new map instance: nextbillion.setApiKey("your_api_key")',
      )
    }

    GA.event({
      category: 'web_sdk_2',
      action: 'map_instance_created',
      label: '2.0',
    })

    this._options = Object.assign({}, options)

    const mapboxOptions: MaplibreGL.MapOptions = {
      transformRequest,
      ...options,
    }

    this._map = new MaplibreGL.Map(mapboxOptions)

    const attrib = new MaplibreGL.AttributionControl({
      customAttribution: options.hideAttribution
        ? '<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>'
        : '<a href="https://nextbillion.ai" target="_blank">&copy; NextBillion.ai</a> <a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>',
    })
    this._map._controls[0] && this._map.removeControl(this._map._controls[0])
    this._map.addControl(attrib)
  }
  get map(): MaplibreGL.Map {
    return this._map
  }

  setStyle(
    style: StyleSpecification | string | null,
    options?: Parameters<MaplibreGL.Map['setStyle']>[1],
  ): this {
    this._map.setStyle(style, options)
    return this
  }

  on(type: string, listener: MaplibreGL.Listener): this {
    // maplibre-gl 5.x .on() returns Subscription, but we maintain the old
    // chainable API for backward compatibility
    this._map.on(type as any, listener)
    return this
  }
}

function transformRequest(url: string, resourceType?: MaplibreGL.ResourceType): MaplibreGL.RequestParameters | undefined {
  const key = config.API_KEY
  if (config.DISABLE_NB_TRANSFORM) {
    return { url }
  }
  if (
    (resourceType === 'SpriteJSON' ||
      resourceType === 'SpriteImage' ||
      resourceType === 'Tile' ||
      resourceType === 'Source' ||
      resourceType === 'Glyphs' ||
      resourceType === 'Style') &&
    key
  ) {
    const apiKeyHeaderName = config.API_KEY_HEADER_NAME
    if (apiKeyHeaderName) {
      return {
        url,
        headers: {
          [apiKeyHeaderName]: key,
        },
      }
    } else {
      return {
        url: urlAddOrReplaceParam(url, { key: key }),
      }
    }
  }
  return { url }
}

function urlAddOrReplaceParam(baseUrl: string, params: { [key: string]: string }): string {
  const url = new URL(baseUrl)
  for (const key in params) {
    url.searchParams.set(key, params[key])
  }
  return url.toString()
}
